import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '../ui/Dialog';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import { TEST_ALLOCATION_RULE } from '../../../../api/queries/allocation';
import { stagingOptions } from '../../../../constants/apollo';
import { AllocationRule } from '../../types';

interface TestResult {
  matches: boolean;
  result?: {
    Result?: string;
    result?: string;
    ResultData?: string;
    resultData?: string;
  };
  conditionResults?: Array<{
    matched: boolean;
    condition: string;
  }>;
}

interface TestRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: AllocationRule | null;
}

export const TestRuleDialog: React.FC<TestRuleDialogProps> = ({
  open,
  onOpenChange,
  rule,
}) => {
  const [orderId, setOrderId] = useState<string>('');
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [testRuleMutation] = useMutation(TEST_ALLOCATION_RULE, stagingOptions);

  const getErrorMessage = (error: any): string => {
    const graphQLError = error?.graphQLErrors?.[0];
    const networkError = error?.networkError;

    if (graphQLError?.message) {
      const message = graphQLError.message;

      if (message.includes("Couldn't find Order") || message.includes("Couldn't find order")) {
        const orderIdMatch = message.match(/id['"]?=(\d+)/);
        const extractedOrderId = orderIdMatch ? orderIdMatch[1] : orderId;
        return `Order ID "${extractedOrderId}" not found. Please use a valid order ID that exists in the system.`;
      }

      if (message.includes("undefined method") || message.includes("nil:NilClass")) {
        return 'Order not found or invalid. Please verify the order ID exists in the system.';
      }

      return message;
    }

    if (networkError?.message) {
      return `Network error: ${networkError.message}`;
    }

    return error?.message || 'Unable to test this rule. Please check the order ID and try again.';
  };

  const handleTest = async () => {
    if (!orderId || !rule || isTesting) return;

    const trimmedOrderId = orderId.trim();
    if (!trimmedOrderId) {
      setTestError('Please enter a valid order ID.');
      return;
    }

    setTestError(null);
    setTestResult(null);
    setIsTesting(true);

    try {
      const response = await testRuleMutation({
        variables: {
          input: {
            ruleId: rule.id,
            orderId: trimmedOrderId,
          },
        },
      });

      const apiResult = response?.data?.testAllocationRule?.testResult;

      if (!apiResult) {
        throw new Error('No test result returned from the server.');
      }

      setTestResult(apiResult);
      toast.success(apiResult.matches ? 'Rule matches the provided order.' : 'Rule did not match this order.');
    } catch (error) {
      console.error('Failed to test rule', error);
      const errorMessage = getErrorMessage(error);
      setTestResult(null);
      setTestError(errorMessage);

      const toastMessage = errorMessage.includes('not found')
        ? 'Order not found. Please use a valid order ID.'
        : 'Test failed. Please check the error details below.';
      toast.error(toastMessage);
    } finally {
      setIsTesting(false);
    }
  };

  const handleClose = () => {
    setOrderId('');
    setTestResult(null);
    setTestError(null);
    setIsTesting(false);
    onOpenChange(false);
  };

  if (!rule) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogClose onClose={handleClose} />
        <DialogHeader>
          <DialogTitle>Test Rule: {rule.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className='text-[#666] font-semibold mb-2'>Order ID</p>
            <p className='text-xs text-gray-500 mb-3'>
              Enter a valid order ID that exists in the system to test this rule against.
            </p>
            <div className='flex flex-col space-y-3'>
              <div className='flex space-x-3'>
                <div className="flex-1">
                  <Input
                    label=""
                    value={orderId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setOrderId(e.target.value);
                      if (testError) setTestError(null);
                    }}
                    placeholder="e.g., 12345, 67890"
                    disabled={isTesting}
                    required={false}
                    error={testError || undefined}
                  />
                </div>
                <Button
                  onClick={handleTest}
                  disabled={!orderId.trim() || isTesting}
                  className="min-w-[105px]"
                >
                  {isTesting ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                      Testing...
                    </span>
                  ) : (
                    'Test Rule'
                  )}
                </Button>
              </div>
              {testError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <svg
                      className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-red-800 mb-1">Test Failed</p>
                      <p className="text-sm text-red-700">{testError}</p>
                      {testError && testError.includes('not found') && (
                        <p className="text-xs text-red-600 mt-2">
                          💡 Tip: Make sure the order ID exists in your system. You can find valid order IDs in the Orders section.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {testResult && (
            <div className="space-y-4">
              <Card>
                <CardContent className="">
                  <div className="flex items-center justify-between pt-4 px-1">
                    <h3 className="font-semibold">Test Result</h3>
                    <Badge variant={testResult?.matches ? 'success' : 'destructive'}>
                      {testResult?.matches ? 'Matched' : 'Not Matched'}
                    </Badge>
                  </div>

                  <div className="space-y-1 px-1">
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        Allocation Result:
                      </span>
                      <div className="mt-1 flex items-center gap-2">
                        Result Via: <Badge variant="default">
                          {testResult?.result?.Result || testResult?.result?.result || 'N/A'}
                        </Badge>
                        Result Data: <span className="text-sm text-gray-600">
                          {testResult?.result?.ResultData ||
                            testResult?.result?.resultData ||
                            ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent >
                  <h3 className="font-semibold mb-4 pt-4">Condition Results</h3>
                  <div className="space-y-2">
                    {testResult?.conditionResults?.length ? testResult.conditionResults.map((condition: any, index: number) => (
                      <div
                        key={index}
                        className={`p-3 rounded border ${condition.matches
                            ? 'bg-green-50 border-green-200'
                            : 'bg-red-50 border-red-200'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">
                            {condition.condition}
                          </span>
                          <Badge variant={condition.matches ? 'success' : 'destructive'}>
                            {condition.matches ? 'Match' : 'No Match'}
                          </Badge>
                        </div>
                        <div className="text-xs space-y-1">
                          <div>
                            <span className="text-gray-600">Expected: </span>
                            <span className="font-mono">{condition.expectedValue}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Actual: </span>
                            <span className="font-mono">{condition.actualValue}</span>
                          </div>
                        </div>
                      </div>
                    )) : (
                      <div className="text-sm text-gray-500">
                        No condition level details were returned.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

